import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { InputWithConfirm, RadioOptionsWithConfirm, IQuestion, CurrencyInputWithConfirm } from 'shared-components';
import { ApplicationState } from '../../../../store';
import * as StudentActions from '../../../../store/student/actions';
import * as PCIActions from '../../../../store/student/preCasInterview/actions';
import { createNavigateForwardAction, createNavigateToAction } from '../../../../middleware/actions';
import { Pages } from '../../../../store/student/preCasInterview/types';
import { toggleTaskMenu } from '../../../../store/ui/actions';
import QuestionPage from '../../../templates/question';
import { Q } from '../../../../store/student/types';
import { titleAppend } from '../../../../utilities/head';

const InterviewQPage4 = () => {
  const dispatch = useDispatch();
  const pageTitle = 'Pre-CAS Questionnaire';
  const currentStep = 4;
  const steps = 4;

  const onNext = () => {
    dispatch(StudentActions.savePreCasInterview(Pages.PAGE4));
    dispatch(createNavigateForwardAction());
  };
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(PCIActions.createUpdatePCIFieldAction(fieldName, value, refValue));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  const {
    liveWhileStudying = '',
    liveWhileStudyingOther = '',
    expectedWeeklyRent = 0,
    commutePlan = '',
    expectedLivingExpenses = 0,
    awareOfUKVIInterview = undefined
  } = useSelector((state: ApplicationState) => state.student.preCasInterview);

  const [complete, setComplete] = useState(false);

  useEffect(() => {
    const isComplete =
      liveWhileStudying !== '' &&
      (liveWhileStudying === 'Other' ? liveWhileStudyingOther !== '' : true) &&
      Number(expectedWeeklyRent) > 0 &&
      commutePlan !== '' &&
      Number(expectedLivingExpenses) > 0 &&
      awareOfUKVIInterview !== undefined;

    setComplete(isComplete);
  }, [
    liveWhileStudying,
    liveWhileStudyingOther,
    expectedWeeklyRent,
    commutePlan,
    expectedLivingExpenses,
    awareOfUKVIInterview
  ]);

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onNext={onNext}
      onBackToTaskList={onBackToTaskList}
      section={pageTitle}
    >
      <Helmet>
        <title>
          {`${pageTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      <RadioOptionsWithConfirm
        label="Where do you plan to live while studying?"
        id={Q.LIVE_WHILE_STUDYING}
        key={Q.LIVE_WHILE_STUDYING}
        questions={[
          {
            char: '1',
            label: 'On-campus accommodation',
            selected: liveWhileStudying === 'On-campus accommodation'
          },
          {
            char: '2',
            label: 'Private accommodation',
            selected: liveWhileStudying === 'Private accommodation'
          },
          {
            char: '3',
            label: 'With friends or family in the UK',
            selected: liveWhileStudying === 'With friends or family in the UK'
          },
          {
            char: '4',
            label: 'I have not decided yet',
            selected: liveWhileStudying === 'I have not decided yet'
          },
          {
            char: '5',
            label: 'Other',
            selected: liveWhileStudying === 'Other'
          }
        ]}
        onClick={(question: IQuestion) => onChange(Q.LIVE_WHILE_STUDYING, question.label)}
      />

      {liveWhileStudying === 'Other' && (
        <InputWithConfirm
          key={Q.MAX_WORK_HOURS_PER_WEEK}
          id={Q.MAX_WORK_HOURS_PER_WEEK}
          value={liveWhileStudyingOther}
          label={'Please provide more information'}
          onChange={(event: React.FormEvent<HTMLInputElement>) =>
            onChange(Q.LIVE_WHILE_STUDYING_OTHER, event.currentTarget.value)
          }
        />
      )}

      <CurrencyInputWithConfirm
        id={Q.EXPECTED_WEEKLY_RENT}
        value={expectedWeeklyRent}
        onChange={(event: React.FormEvent<HTMLInputElement>) => {
          const value = Number(event.currentTarget.value) || 0;
          onChange(Q.EXPECTED_WEEKLY_RENT, value);
        }}
        label="How much do you expect to pay for accommodation per week?"
      />

      <RadioOptionsWithConfirm
        label="How will you travel between your accommodation and the university campus?"
        id={Q.COMMUTE_PLAN}
        key={Q.COMMUTE_PLAN}
        questions={[
          {
            char: '1',
            label: 'Walk',
            selected: commutePlan === 'Walk'
          },
          {
            char: '2',
            label: 'Bicycle',
            selected: commutePlan === 'Bicycle'
          },
          {
            char: '3',
            label: 'Public transport e.g. bus or train',
            selected: commutePlan === 'Public transport e.g. bus or train'
          },
          {
            char: '4',
            label: 'Drive e.g. taxi, my own car',
            selected: commutePlan === 'Drive e.g. taxi, my own car'
          },
          {
            char: '5',
            label: 'I am not sure',
            selected: commutePlan === 'I am not sure'
          }
        ]}
        onClick={(question: IQuestion) => onChange(Q.COMMUTE_PLAN, question.label)}
      />

      <CurrencyInputWithConfirm
        id={Q.EXPECTED_LIVING_EXPENSES}
        value={expectedLivingExpenses}
        onChange={(event: React.FormEvent<HTMLInputElement>) => {
          const value = Number(event.currentTarget.value) || 0;
          onChange(Q.EXPECTED_LIVING_EXPENSES, value);
        }}
        label="How much do you expect to spend in living expenses per week? (e.g an amount to cover food, daily travel, school expenses, phone, socialising etc.)"
      />

      <RadioOptionsWithConfirm
        label="International students can be interviewed by UKVI prior to receiving a visa, were you aware of this?"
        id={Q.AWARE_OF_UK_VI_INTERVIEW}
        key={Q.AWARE_OF_UK_VI_INTERVIEW}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: awareOfUKVIInterview === true
          },
          {
            char: 'N',
            label: 'No',
            selected: awareOfUKVIInterview === false
          }
        ]}
        maxQuestions={2}
        isInline={true}
        onClick={(question: IQuestion) => onChange(Q.AWARE_OF_UK_VI_INTERVIEW, question.label === 'Yes')}
      />
    </QuestionPage>
  );
};

export default InterviewQPage4;
